import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  Container,
  Button,
  Alert,
  IconButton,
  Pagination,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReportContext } from "./ReportContext";
import { AuthContext } from "../auth/AuthContext";
import LoadingComponent from "../utils/LoadingComponent";
import { showSwalNotification } from "../utils/SwalResponse";
import ReportsGridPreview from "./ReportsGridPreview";
import ReportsViewToggle from "./ReportsViewToggle";

const Reports = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const { fetchReports, loading } = useContext(ReportContext);
  const { authToken } = useContext(AuthContext);

  // Pagination states
  const [page, setPage] = useState(1);
  const reportsPerPage = 6; // Adjust how many to show per page

  useEffect(() => {
    const handleFetch = async (type) => {
      const response = await fetchReports(type);
      if (response?.status === "success") {
        setReports(response.data);
        setPage(1); // reset to first page when refetching
      } else {
        showSwalNotification(response);
        navigate(-1);
      }
    };

    if (type && authToken) {
      handleFetch(type);
    }
  }, [type, authToken]);

  const getTitle = () => {
    switch (type) {
      case "all":
        return "My Reports";
      case "pending":
        return "Pending Reports";
      default:
        return "All Reports";
    }
  };

  if (!!loading) return <LoadingComponent />;

  // Pagination logic
  const totalPages = Math.ceil(reports.length / reportsPerPage);
  const paginatedReports = Array.isArray(reports) && reports.slice(
    (page - 1) * reportsPerPage,
    page * reportsPerPage
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, position: "relative" }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            {getTitle()}
          </Typography>
          <IconButton
            onClick={() => navigate(-1)}
            color="error"
            sx={{ border: "1px solid", borderColor: "error.main" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Reports Grid */}
        {Array.isArray(reports) && reports.length > 0 ? (
          <>
            {/* <ReportsGridPreview reports={paginatedReports} /> */}
            <ReportsViewToggle reports={paginatedReports} />

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={4}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                  shape="rounded"
                  size="medium"
                />
              </Box>
            )}
          </>
        ) : (
          <Alert severity="info">
            No {type === "pending" ? "pending" : ""} reports found.
          </Alert>
        )}
      </motion.div>
    </Container>
  );
};

export default Reports;
